import { isAssistantRtl } from '@/domain/assistant/assistant-direction';
import { groupRuleText } from '@/domain/catalog/option-groups';
import {
  capitalize,
  scheduleBadgeText,
} from '@/domain/catalog/catalog-status-text';
import type { TTranslate } from '@/domain/catalog/catalog-status-text';
import { scheduleSummary } from '@/domain/catalog/schedule-form';
import { OptionType } from '@/enums/OptionType';
import { StockUnit } from '@/enums/StockUnit';
import type {
  IAssistantRow,
  IAvailabilityValue,
  IFlagValue,
  IGroupLinksValue,
  IGroupRulesValue,
  INewChoiceValue,
  INewGroupValue,
  INewStockItemValue,
  IOrderValue,
  IPriceValue,
  IRecipeValue,
  IScheduleValue,
  ISizesValue,
  IStockMoveValue,
  IStockQuantityValue,
  TAssistantValue,
  TRowEntity,
  TStockUnit,
} from '@/interfaces/assistant.interface';
import { roundPrice } from '@/utils';
import type { IGhDiffValue } from '../gpthelp-host-contract';
import { bidiIsolateParams, cardTextLength, clampText } from './card-text';
import type { ICardFormat, ICardTextParams } from './card-text';
import {
  isAvailabilityValue,
  isCopyValue,
  isCreateValue,
  isFlagValue,
  isGroupLinksValue,
  isGroupRulesValue,
  isMoneyValue,
  isNewCategoryValue,
  isNewChoiceValue,
  isNewGroupValue,
  isNewStockItemValue,
  isOrderValue,
  isPlacementValue,
  isPriceValue,
  isRecipeValue,
  isRemovedValue,
  isScheduleValue,
  isSizesValue,
  isStockMoveValue,
  isStockQuantityValue,
  isTextValue,
  rowItemLabel,
  singleProductRow,
} from './row-values';

/**
 * How the cards show the rows of one field (plan F10): the diff card, the
 * activity rows, the stale lines and the receipts all read a row through
 * `presenterOf`, so a row of a later phase never throws and never shows a
 * value this release cannot read.
 */
export interface IRowPresenter {
  /** i18n key of the field title of a single-row card («Precio»). */
  fieldKey: string;
  /** The diff value; null = nothing to show (an empty text before). */
  diff(
    value: TAssistantValue,
    row: IAssistantRow,
    fmt: ICardFormat
  ): IGhDiffValue | null;
  /** Plain text for activity rows, stale lines and receipts; null = label only. */
  text(
    value: TAssistantValue,
    row: IAssistantRow,
    fmt: ICardFormat
  ): string | null;
  /** «+$ 1.400», «+ Leche»; null = none. */
  delta?(row: IAssistantRow, fmt: ICardFormat): string | null;
  /**
   * What the row's value adds under its title, after where its subject is
   * («Se crea oculta»); absent or null: nothing.
   */
  subtitle?(row: IAssistantRow, fmt: ICardFormat): string | null;
  /**
   * The stale line of a value that changed since it was seen («ahora $ 3.600»);
   * absent or null: the line only says that it changed (a text can be long).
   * `row`: the value's row in the fresh proposal; null when it left it.
   */
  stale?(
    value: TAssistantValue,
    row: IAssistantRow | null,
    fmt: ICardFormat
  ): string | null;
  /**
   * The row's value names its size (`sizeName`), so its label and field
   * title leave it out («+ Grande $ 11.000» under «Napolitana»); absent or
   * false: they name it.
   */
  namesSize?(row: IAssistantRow): boolean;
  /**
   * The row's subject alone does not say what changes («Napolitana» next to
   * «Napolitana · Grande»), so where the row is titled by its subject, its
   * field title goes with it: under the title in a card (a title is cut to
   * one line, a subtitle wraps), after the label where nothing goes under it.
   */
  subjectNeedsField?: boolean;
}

/** Host contract §3.3 caps of a diff value and its difference. */
const PILL_MAX = 24;
const TEXT_VALUE_MAX = 1000;
const DELTA_MAX = 24;

/**
 * Entities whose `price` rows hold an `IPriceValue` that is what customers
 * pay for the product (plan F3: products, sizes); a choice's is a surcharge
 * (plan P3-4).
 */
const PRICE_ENTITIES: readonly TRowEntity[] = ['item', 'size'];

/**
 * Entities whose `availability` rows hold an `IAvailabilityValue` shown as
 * «Disponible» / «Oculto» (plan F3: products, categories); an option choice
 * and a size are paused instead (plan P3-4, P4-1).
 */
const AVAILABILITY_ENTITIES: readonly TRowEntity[] = ['item', 'category'];

/** Entities that are paused, never hidden (plan P3-4: choices; P4-1: sizes). */
const PAUSED_ENTITIES: readonly TRowEntity[] = ['choice', 'size'];

/**
 * Entities whose `order` rows this release reads (plan P2-4: a category's
 * products, a menu's categories; P3-4: a group's choices; later phases add
 * theirs). A product's sizes (P4-1) have their own presenter.
 */
const ORDER_ENTITIES: readonly TRowEntity[] = ['category', 'menu', 'group'];

/** Entities whose `schedule` rows this release reads (plan P4-2). */
const SCHEDULE_ENTITIES: readonly TRowEntity[] = ['item', 'category'];

/** The names an order row shows; the rest are counted (plan P2-4). */
const ORDER_NAMES_SHOWN = 8;

/** Entities of products: a product and its sizes. */
const PRODUCT_ENTITIES: readonly TRowEntity[] = ['item', 'size'];

/** A row's entity; rows stored before Phase 1 carry none (plan F3). */
export function rowEntity(row: IAssistantRow): TRowEntity {
  return row.entity ?? (typeof row.sizeId === 'number' ? 'size' : 'item');
}

/**
 * The row changes a product or one of its sizes; any other row (a category,
 * a menu's order…) makes the cards speak of changes (plan P2-4).
 */
export function isProductRow(row: IAssistantRow): boolean {
  return PRODUCT_ENTITIES.includes(rowEntity(row));
}

/** The row creates an option group (plan P3-1: `newGroup:{n}`). */
export function isNewGroupRow(row: IAssistantRow): boolean {
  return row.field === 'newGroup' && rowEntity(row) === 'group';
}

/**
 * The existing option group whose content a row changes (plan P3-1: a group
 * or choice row carries its `groupId`); null for any other row, a new
 * group's included.
 */
export function changedGroupId(row: IAssistantRow): number | null {
  const entity = rowEntity(row);
  if ((entity !== 'group' && entity !== 'choice') || isNewGroupRow(row))
    return null;
  return typeof row.groupId === 'number' ? row.groupId : null;
}

/**
 * The row changes a product's sizes (plan P4-1): a switch to sizes or back to
 * one price, a new size, a size's name, pause or removal, or their order. A
 * size's price is a price row.
 */
export function isSizeRow(row: IAssistantRow): boolean {
  const entity = rowEntity(row);
  switch (row.field) {
    case 'sizes':
    case 'newSize':
      return true;
    case 'name':
    case 'availability':
    case 'remove':
      return entity === 'size';
    case 'order':
      return entity === 'item';
    default:
      return false;
  }
}

/**
 * The product whose sizes every row changes (plan P4-1: its receipt names
 * it, whatever the size ops); null when a row changes something else or
 * another product, or there are no rows.
 */
export function sizesProductName(rows: IAssistantRow[]): string | null {
  if (!rows.every(isSizeRow)) return null;
  return singleProductRow(rows)?.itemName ?? null;
}

/**
 * The row changes an option group itself (plan P3-1): a new group, its
 * name, description, rules or choice order, or one of its choices; a
 * product's link list is the product's.
 */
export function isOptionGroupRow(row: IAssistantRow): boolean {
  const entity = rowEntity(row);
  return entity === 'group' || entity === 'choice';
}

/** Entities of inventory rows (plan P5-1): a stock item, a recipe. */
const STOCK_ENTITIES: readonly TRowEntity[] = ['stockItem', 'recipe'];

/**
 * The row changes the inventory (plan P5-1): a stock item's quantity, a new
 * stock item, a stock item's own fields, or a recipe.
 */
export function isStockRow(row: IAssistantRow): boolean {
  return STOCK_ENTITIES.includes(rowEntity(row));
}

/**
 * The option group of the choice whose recipe a row sets (plan P5-1
 * `recipe:choice:{cid}`, the row carrying the choice's `groupId`): every
 * product using that group uses the recipe. Null for any other row.
 */
export function recipeGroupId(row: IAssistantRow): number | null {
  return row.field === 'recipe' &&
    rowEntity(row) === 'recipe' &&
    typeof row.choiceId === 'number' &&
    typeof row.groupId === 'number'
    ? row.groupId
    : null;
}

/**
 * «$ 5.999», or «$ 5.999 · con descuento $ 5.399» when customers pay a
 * discounted price; `discountKey` words it otherwise (a size inside a list of
 * sizes: «$ 5.999 (con descuento $ 5.399)»).
 */
function priceText(
  value: Pick<IPriceValue, 'price' | 'discountedPrice'>,
  fmt: ICardFormat,
  discountKey = 'assistant.proposal.value.priceDiscount'
): string {
  if (value.discountedPrice === null) return fmt.money(value.price);
  return fmt.t(discountKey, {
    price: fmt.money(value.price),
    discounted: fmt.money(value.discountedPrice),
  });
}

/** What customers pay: the discounted price when there is one. */
export function paidPrice(value: IPriceValue): number {
  return value.discountedPrice ?? value.price;
}

/**
 * «Disponible» / «Oculto»; a category is «Oculta» (plan F0: category copy
 * is feminine). Without its row, a value reads as a product's.
 */
function availabilityText(
  value: IAvailabilityValue,
  row: IAssistantRow | null,
  fmt: ICardFormat
): string {
  if (value.available) return fmt.t('assistant.proposal.value.visible');
  return fmt.t(
    row && rowEntity(row) === 'category'
      ? 'menuPage.categoryStatus.hidden'
      : 'assistant.proposal.value.hidden'
  );
}

/**
 * A row this release does not know (a field of a later phase): «Cambia» and
 * the row's label, never a value it cannot read.
 */
export const GENERIC_PRESENTER: IRowPresenter = {
  fieldKey: 'assistant.proposal.field.change',
  // The pill stands for the whole change, so it shows once: as the value after.
  diff: (value, row, fmt) =>
    value === row.to
      ? {
          pill: clampText(fmt.t('assistant.proposal.value.changed'), PILL_MAX),
          tone: 'neutral',
        }
      : null,
  text: () => null,
};

/**
 * «+$ 1.400»: how much what customers pay moves (none when only a regular
 * price under a kept discount does).
 */
function paidPriceDelta(row: IAssistantRow, fmt: ICardFormat): string | null {
  if (!row.from || !isPriceValue(row.from) || !isPriceValue(row.to))
    return null;
  const change = roundPrice(paidPrice(row.to) - paidPrice(row.from));
  if (!change) return null;
  return clampText(
    `${change > 0 ? '+' : '−'}${fmt.money(Math.abs(change))}`,
    DELTA_MAX
  );
}

const PRICE_PRESENTER: IRowPresenter = {
  fieldKey: 'assistant.proposal.field.price',
  diff: (value, row, fmt) =>
    isPriceValue(value)
      ? { text: clampText(priceText(value, fmt), TEXT_VALUE_MAX) }
      : GENERIC_PRESENTER.diff(value, row, fmt),
  text: (value, _row, fmt) =>
    isPriceValue(value) ? priceText(value, fmt) : null,
  delta: paidPriceDelta,
  stale: (value, _row, fmt) =>
    isPriceValue(value)
      ? fmt.t('assistant.result.staleNow', { price: priceText(value, fmt) })
      : null,
};

/**
 * «+$ 500» / «Sin cargo»: what an option choice adds to the product's price
 * (plan P3-4: a choice price is a surcharge, 0 is no charge).
 */
function surchargeText(price: number, fmt: ICardFormat): string {
  return price === 0
    ? fmt.t('assistant.proposal.value.noCharge')
    : fmt.t('assistant.proposal.value.surcharge', { price: fmt.money(price) });
}

/**
 * «+$ 400», or «sin cargo» inside the sentence: what customers pay for a
 * discounted choice.
 */
function discountedSurchargeText(price: number, fmt: ICardFormat): string {
  return price === 0
    ? fmt.t('assistant.proposal.value.noChargeInline')
    : fmt.t('assistant.proposal.value.surcharge', { price: fmt.money(price) });
}

/**
 * «+$ 500», or «+$ 500 · con descuento +$ 400» when the choice is discounted:
 * a discount of 0 is what customers pay, «… · con descuento sin cargo» (a
 * copy keeps a stored one, API P3-API-M2 review finding 2). A discount equal
 * to the price (a free choice with a discount of 0) changes nothing
 * customers pay.
 */
function choicePriceText(
  value: Pick<IPriceValue, 'price' | 'discountedPrice'>,
  fmt: ICardFormat
): string {
  const price = surchargeText(value.price, fmt);
  if (value.discountedPrice === null || value.discountedPrice === value.price)
    return price;
  return fmt.t('assistant.proposal.value.priceDiscount', {
    price,
    discounted: discountedSurchargeText(value.discountedPrice, fmt),
  });
}

/** An option choice's price (plan P3-4): a surcharge; the delta is what customers pay. */
const CHOICE_PRICE_PRESENTER: IRowPresenter = {
  fieldKey: 'assistant.proposal.field.price',
  diff: (value, row, fmt) =>
    isPriceValue(value)
      ? { text: clampText(choicePriceText(value, fmt), TEXT_VALUE_MAX) }
      : GENERIC_PRESENTER.diff(value, row, fmt),
  text: (value, _row, fmt) =>
    isPriceValue(value) ? choicePriceText(value, fmt) : null,
  delta: paidPriceDelta,
  stale: (value, _row, fmt) =>
    isPriceValue(value)
      ? fmt.t('assistant.result.staleNow', {
          price: choicePriceText(value, fmt),
        })
      : null,
};

const AVAILABILITY_PRESENTER: IRowPresenter = {
  fieldKey: 'assistant.proposal.field.availability',
  diff: (value, row, fmt) =>
    isAvailabilityValue(value)
      ? {
          pill: clampText(availabilityText(value, row, fmt), PILL_MAX),
          tone: value.available ? 'success' : 'neutral',
        }
      : GENERIC_PRESENTER.diff(value, row, fmt),
  text: (value, row, fmt) =>
    isAvailabilityValue(value) ? availabilityText(value, row, fmt) : null,
  stale: (value, row, fmt) =>
    isAvailabilityValue(value)
      ? fmt.t('assistant.result.staleState', {
          state: availabilityText(value, row, fmt),
        })
      : null,
};

/**
 * «Disponible» / «Pausada»: an option choice or a size is paused, never
 * hidden (plan P3-4, P4-1).
 */
function pausedStateText(value: IAvailabilityValue, fmt: ICardFormat): string {
  return fmt.t(
    value.available
      ? 'assistant.proposal.value.visible'
      : 'assistant.proposal.value.paused'
  );
}

/**
 * Whether an option choice or a size can be chosen (plan P3-4, P4-1):
 * «Disponible» or «Pausada».
 */
const PAUSED_AVAILABILITY_PRESENTER: IRowPresenter = {
  fieldKey: 'assistant.proposal.field.availability',
  diff: (value, row, fmt) =>
    isAvailabilityValue(value)
      ? {
          pill: clampText(pausedStateText(value, fmt), PILL_MAX),
          tone: value.available ? 'success' : 'neutral',
        }
      : GENERIC_PRESENTER.diff(value, row, fmt),
  text: (value, _row, fmt) =>
    isAvailabilityValue(value) ? pausedStateText(value, fmt) : null,
  stale: (value, _row, fmt) =>
    isAvailabilityValue(value)
      ? fmt.t('assistant.result.staleState', {
          state: pausedStateText(value, fmt),
        })
      : null,
};

/** A name or a description: the text itself; an empty one is nothing to show. */
function textPresenter(fieldKey: string): IRowPresenter {
  return {
    fieldKey,
    diff(value, row, fmt) {
      if (!isTextValue(value)) return GENERIC_PRESENTER.diff(value, row, fmt);
      return value.text
        ? { text: clampText(value.text, TEXT_VALUE_MAX) }
        : null;
    },
    text: (value) => (isTextValue(value) ? value.text || null : null),
  };
}

const NAME_PRESENTER = textPresenter('assistant.proposal.field.name');
const DESCRIPTION_PRESENTER = textPresenter(
  'assistant.proposal.field.description'
);

/**
 * A new product: its price (a single-row card titles the row with its name),
 * and that it starts hidden when it does (plan P2-4).
 */
const CREATE_PRESENTER: IRowPresenter = {
  fieldKey: 'assistant.proposal.field.price',
  diff: (value, row, fmt) =>
    isCreateValue(value)
      ? {
          text: clampText(
            priceText(
              {
                price: value.price,
                discountedPrice: value.discountedPrice ?? null,
              },
              fmt
            ),
            TEXT_VALUE_MAX
          ),
        }
      : GENERIC_PRESENTER.diff(value, row, fmt),
  text: (value, _row, fmt) =>
    isCreateValue(value)
      ? priceText(
          {
            price: value.price,
            discountedPrice: value.discountedPrice ?? null,
          },
          fmt
        )
      : null,
  subtitle: (row, fmt) =>
    isCreateValue(row.to) && row.to.visible === false
      ? fmt.t('assistant.proposal.value.newHidden')
      : null,
};

/**
 * A new category (plan P2-4): its name, and that it starts hidden when it
 * does. Its row names the field: its label is the name already, so the
 * activity says that it is new.
 */
const NEW_CATEGORY_PRESENTER: IRowPresenter = {
  fieldKey: 'assistant.proposal.field.newCategory',
  diff: (value, row, fmt) =>
    isNewCategoryValue(value)
      ? { text: clampText(value.name, TEXT_VALUE_MAX) }
      : GENERIC_PRESENTER.diff(value, row, fmt),
  text: (value, _row, fmt) =>
    isNewCategoryValue(value)
      ? fmt.t('assistant.proposal.field.newCategory')
      : null,
  subtitle: (row, fmt) =>
    isNewCategoryValue(row.to) && !row.to.visible
      ? fmt.t('assistant.proposal.value.newCategoryHidden')
      : null,
};

/** «Solo en el Punto de Venta» / «En la carta y en el Punto de Venta»: the Menú's own words. */
function whereText(value: IFlagValue, fmt: ICardFormat): string {
  return fmt.t(
    value.on ? 'menuPage.where.posOnly' : 'menuPage.where.everywhere'
  );
}

/** Where a category is sold (plan P2-4): titled like the Menú's «¿Dónde se ve?». */
const POS_ONLY_PRESENTER: IRowPresenter = {
  fieldKey: 'menuPage.where.title',
  diff: (value, row, fmt) =>
    isFlagValue(value)
      ? { text: clampText(whereText(value, fmt), TEXT_VALUE_MAX) }
      : GENERIC_PRESENTER.diff(value, row, fmt),
  text: (value, _row, fmt) =>
    isFlagValue(value) ? whereText(value, fmt) : null,
  stale: (value, _row, fmt) =>
    isFlagValue(value)
      ? fmt.t('assistant.result.staleState', { state: whereText(value, fmt) })
      : null,
};

/** A product moved to another category (plan P2-4): the category it is in, then the one it goes to. */
const MOVE_PRESENTER: IRowPresenter = {
  fieldKey: 'assistant.proposal.field.category',
  diff(value, row, fmt) {
    if (!isPlacementValue(value))
      return GENERIC_PRESENTER.diff(value, row, fmt);
    return value.categoryName
      ? { text: clampText(value.categoryName, TEXT_VALUE_MAX) }
      : null;
  },
  text: (value) =>
    isPlacementValue(value) ? value.categoryName || null : null,
  stale: (value, _row, fmt) =>
    isPlacementValue(value) && value.categoryName
      ? fmt.t('assistant.result.staleState', { state: value.categoryName })
      : null,
};

/**
 * «Flan, Helado, Tiramisú»: the first 8 names in their new order, then
 * «y {n} más» for the rest (the value names the first 12 of its ids).
 */
function orderText(value: IOrderValue, fmt: ICardFormat): string | null {
  const shown = value.names.slice(0, ORDER_NAMES_SHOWN);
  if (!shown.length) return null;
  const list = shown.join(', ');
  const more = value.ids.length - shown.length;
  return more > 0
    ? fmt.t('assistant.proposal.value.orderMore', {
        list,
        n: fmt.number(more),
      })
    : list;
}

/** Where a reorder puts the ids it moves: before or after the others. */
type TOrderEnd = 'first' | 'last';

/** The text of the names a reorder moves, by where they go. */
const ORDER_END_KEYS: Readonly<Record<TOrderEnd, string>> = {
  first: 'assistant.proposal.value.orderToStart',
  last: 'assistant.proposal.value.orderToEnd',
};

/**
 * The fewest ids that `to` puts at `end` of the list, in their new order,
 * with every other id kept in its order of `from` (what a reorder with that
 * placement does, plan P2-1); null when the lists hold different ids or
 * only moving every id explains it.
 */
function idsMovedTo(
  end: TOrderEnd,
  from: readonly number[],
  to: readonly number[]
): number[] | null {
  if (from.length !== to.length) return null;
  for (let count = 1; count < to.length; count++) {
    const split = end === 'first' ? count : to.length - count;
    const moved = end === 'first' ? to.slice(0, split) : to.slice(split);
    const kept = end === 'first' ? to.slice(split) : to.slice(0, split);
    const movedIds = new Set(moved);
    const others = from.filter((id) => !movedIds.has(id));
    if (
      others.length === kept.length &&
      others.every((id, index) => id === kept[index])
    )
      return moved;
  }
  return null;
}

/**
 * «Agua, Soda → al final», «Flan → primero»: the names a reorder moves and
 * where they go, for when the first names read the same before and after (a
 * move past them, plan P2-4). The fewest names that explain it; null when
 * neither end does, or a name is not in the values (they name their first
 * 12 ids).
 */
function movedNamesText(
  from: IOrderValue,
  to: IOrderValue,
  fmt: ICardFormat
): string | null {
  const names = new Map<number, string>();
  for (const value of [from, to])
    value.names.forEach((name, index) => names.set(value.ids[index], name));
  const moves = (['last', 'first'] as const)
    .map((end) => ({ end, ids: idsMovedTo(end, from.ids, to.ids) }))
    .filter(
      (move): move is { end: TOrderEnd; ids: number[] } =>
        move.ids !== null && move.ids.every((id) => names.has(id))
    )
    .sort((a, b) => a.ids.length - b.ids.length);
  if (!moves.length) return null;
  const [{ end, ids }] = moves;
  return fmt.t(ORDER_END_KEYS[end], {
    list: ids.map((id) => names.get(id)).join(', '),
  });
}

/**
 * The text of an order value. The new order says which names move and where
 * when its first names read like the old order's.
 */
function orderValueText(
  value: IOrderValue,
  row: IAssistantRow,
  fmt: ICardFormat
): string | null {
  const text = orderText(value, fmt);
  const { from } = row;
  if (value !== row.to || !from || !isOrderValue(from)) return text;
  if (text !== orderText(from, fmt)) return text;
  return movedNamesText(from, value, fmt) ?? text;
}

/** A new order of a category's products or a menu's categories (plan P2-4): one row per list. */
const ORDER_PRESENTER: IRowPresenter = {
  fieldKey: 'assistant.proposal.field.order',
  diff(value, row, fmt) {
    if (!isOrderValue(value)) return GENERIC_PRESENTER.diff(value, row, fmt);
    const text = orderValueText(value, row, fmt);
    return text ? { text: clampText(text, TEXT_VALUE_MAX) } : null;
  },
  text: (value, row, fmt) =>
    isOrderValue(value) ? orderValueText(value, row, fmt) : null,
};

/**
 * A new order of a product's sizes (plan P4-1): read like any order, and
 * named «Orden de las variantes» wherever the row is named, since the
 * product's name alone reads like the label of the product's other size rows.
 */
const SIZE_ORDER_PRESENTER: IRowPresenter = {
  ...ORDER_PRESENTER,
  fieldKey: 'assistant.proposal.field.sizeOrder',
  subjectNeedsField: true,
};

/**
 * «elija 1», «opcional, hasta 2»: an option group's rules in the Opciones
 * editor's own words (`groupRuleText`, plan P3-4), as a sentence reads them.
 */
function ruleWords(rules: IGroupRulesValue, fmt: ICardFormat): string {
  const rule = groupRuleText({
    type: rules.type === 'add' ? OptionType.Add : OptionType.Choose,
    multiple: rules.multiple,
    mandatory: rules.required,
    min: rules.min,
    max: rules.max,
  });
  return fmt.t(rule.key, rule.params);
}

/** «Elija 1»: the rules as a value of their own, capitalized like the editor shows them. */
function rulesText(rules: IGroupRulesValue, fmt: ICardFormat): string {
  return capitalize(ruleWords(rules, fmt));
}

/** An option group's rules (plan P3-4): «Elija 1» → «Opcional, hasta 2». */
const RULES_PRESENTER: IRowPresenter = {
  fieldKey: 'assistant.proposal.field.rules',
  diff: (value, row, fmt) =>
    isGroupRulesValue(value)
      ? { text: clampText(rulesText(value, fmt), TEXT_VALUE_MAX) }
      : GENERIC_PRESENTER.diff(value, row, fmt),
  text: (value, _row, fmt) =>
    isGroupRulesValue(value) ? rulesText(value, fmt) : null,
  stale: (value, _row, fmt) =>
    isGroupRulesValue(value)
      ? fmt.t('assistant.result.staleState', { state: ruleWords(value, fmt) })
      : null,
};

/**
 * The longest a new group's summary gets: what «Cambios del asistente» shows
 * of a value, so the card and the activity read the same text.
 */
const GROUP_SUMMARY_MAX = 160;

/** «Entera», «Almendra +$ 500»: a choice of a new group; a free one by its name alone. */
function newGroupChoiceText(
  choice: INewGroupValue['choices'][number],
  fmt: ICardFormat
): string {
  const free = choice.price === 0 && (choice.discountedPrice ?? 0) === 0;
  const { text } = bidiIsolateParams(fmt.locale, {
    text: free ? choice.name : `${choice.name} ${choicePriceText(choice, fmt)}`,
  });
  return String(text);
}

/**
 * «Elija 1 · Entera, Almendra +$ 500 · 1 pausada»: a new group's rules, its
 * choices with what each adds (the first ones and «y {n} más» when they do
 * not all fit) and how many of them start paused (a copy of a group with a
 * sold-out choice says so, plan P3-4); «Elija 1 · 12 opciones» when not even
 * one choice fits. Each choice shown makes the text longer (it adds at least
 * «, x» and the count it replaces loses at most a digit), so the first one
 * that does not fit ends the list.
 */
function groupSummaryText(value: INewGroupValue, fmt: ICardFormat): string {
  const rule = rulesText(value.rules, fmt);
  const paused = value.choices.filter((choice) => !choice.active).length;
  const pausedText = paused
    ? ` · ${fmt.tc('assistant.proposal.value.pausedCount', paused, {
        n: fmt.number(paused),
      })}`
    : '';
  const withChoices = (choices: string) => `${rule} · ${choices}${pausedText}`;
  const fits = (summary: string) =>
    cardTextLength(summary) <= GROUP_SUMMARY_MAX;
  const choices = value.choices.map((choice) =>
    newGroupChoiceText(choice, fmt)
  );
  const every = withChoices(choices.join(', '));
  if (choices.length && fits(every)) return every;
  let fitting: string | null = null;
  for (let shown = 1; shown < choices.length; shown += 1) {
    const summary = withChoices(
      fmt.t('menuPage.groups.picker.andMore', {
        list: choices.slice(0, shown).join(', '),
        n: fmt.number(choices.length - shown),
      })
    );
    if (!fits(summary)) break;
    fitting = summary;
  }
  if (fitting) return fitting;
  const count = value.choices.length;
  return clampText(
    `${fmt.tc('assistant.proposal.value.groupSummary', count, {
      rule,
      n: fmt.number(count),
    })}${pausedText}`,
    GROUP_SUMMARY_MAX
  );
}

/**
 * A new option group (plan P3-4): its rules and choices; its row is titled by
 * its name (a single-row card: «Grupo nuevo»). The products that get it are
 * their own `groups` rows.
 */
const NEW_GROUP_PRESENTER: IRowPresenter = {
  fieldKey: 'assistant.proposal.field.newGroup',
  diff: (value, row, fmt) =>
    isNewGroupValue(value)
      ? { text: clampText(groupSummaryText(value, fmt), TEXT_VALUE_MAX) }
      : GENERIC_PRESENTER.diff(value, row, fmt),
  text: (value, _row, fmt) =>
    isNewGroupValue(value) ? groupSummaryText(value, fmt) : null,
};

/** «+$ 500», «Sin cargo · Pausada»: a new choice's price, and that it starts paused. */
function newChoiceText(value: INewChoiceValue, fmt: ICardFormat): string {
  const price = choicePriceText(value, fmt);
  return value.active
    ? price
    : `${price} · ${fmt.t('assistant.proposal.value.paused')}`;
}

/** A new option choice (plan P3-4): its price; its row names the group and the choice. */
const NEW_CHOICE_PRESENTER: IRowPresenter = {
  fieldKey: 'assistant.proposal.field.newChoice',
  diff: (value, row, fmt) =>
    isNewChoiceValue(value)
      ? { text: clampText(newChoiceText(value, fmt), TEXT_VALUE_MAX) }
      : GENERIC_PRESENTER.diff(value, row, fmt),
  text: (value, _row, fmt) =>
    isNewChoiceValue(value) ? newChoiceText(value, fmt) : null,
};

/**
 * Something taken out (plan P3-4: a choice of its group; P4-1: a size of its
 * product): its price before (`priceOf`: a choice's surcharge, a size's
 * price), «Se quita» after.
 */
function removePresenter(
  priceOf: (value: IPriceValue, fmt: ICardFormat) => string
): IRowPresenter {
  return {
    fieldKey: 'assistant.proposal.field.remove',
    diff(value, row, fmt) {
      if (isRemovedValue(value))
        return {
          pill: clampText(fmt.t('assistant.proposal.value.removed'), PILL_MAX),
          tone: 'neutral',
        };
      if (isPriceValue(value))
        return { text: clampText(priceOf(value, fmt), TEXT_VALUE_MAX) };
      return GENERIC_PRESENTER.diff(value, row, fmt);
    },
    text(value, _row, fmt) {
      if (isRemovedValue(value))
        return fmt.t('assistant.proposal.value.removed');
      return isPriceValue(value) ? priceOf(value, fmt) : null;
    },
    stale: (value, _row, fmt) =>
      isPriceValue(value)
        ? fmt.t('assistant.result.staleNow', { price: priceOf(value, fmt) })
        : null,
  };
}

const CHOICE_REMOVE_PRESENTER = removePresenter(choicePriceText);
const SIZE_REMOVE_PRESENTER = removePresenter(priceText);

/** The sizes a `sizes` row names; the rest are counted (plan P4-1). */
const SIZES_SHOWN = 6;

/** What separates the sizes of a product in a value. */
const SIZE_SEPARATOR = ' · ';

/**
 * «Chica $ 8.000», «Chica $ 8.000 (con descuento $ 7.000)»: a size and its
 * price; its discount stays inside it, since « · » separates the sizes of a
 * value. The name is isolated like any value in a sentence, so «1,5 L» keeps
 * its order in Arabic.
 */
function sizeText(
  size: Pick<INewChoiceValue, 'name' | 'price' | 'discountedPrice'>,
  fmt: ICardFormat
): string {
  const { name } = bidiIsolateParams(fmt.locale, { name: size.name });
  return `${name} ${priceText(
    size,
    fmt,
    'assistant.proposal.value.sizeDiscount'
  )}`;
}

/**
 * «Chica $ 8.000 · Grande $ 11.000»: a product's sizes in their order, the
 * first 6, then «y {n} más» for the rest (plan P4-1); null for none.
 */
function sizesText(value: ISizesValue, fmt: ICardFormat): string | null {
  const shown = value.sizes.slice(0, SIZES_SHOWN);
  if (!shown.length) return null;
  const list = shown.map((size) => sizeText(size, fmt)).join(SIZE_SEPARATOR);
  const more = value.sizes.length - shown.length;
  return more > 0
    ? fmt.t('assistant.proposal.value.orderMore', {
        list,
        n: fmt.number(more),
      })
    : list;
}

/**
 * A product's price as a `sizes` row reads it (plan P4-1): its sizes, or its
 * one price; undefined for a value of another shape.
 */
function sizesOrPriceText(
  value: TAssistantValue,
  fmt: ICardFormat
): string | null | undefined {
  if (isSizesValue(value)) return sizesText(value, fmt);
  return isPriceValue(value) ? priceText(value, fmt) : undefined;
}

/**
 * A product that switches to sizes or back to one price (plan P4-1): «$ 8.000»
 * ↔ «Chica $ 8.000 · Grande $ 11.000».
 */
const SIZES_PRESENTER: IRowPresenter = {
  fieldKey: 'assistant.proposal.field.sizes',
  diff(value, row, fmt) {
    const text = sizesOrPriceText(value, fmt);
    if (text === undefined) return GENERIC_PRESENTER.diff(value, row, fmt);
    return text ? { text: clampText(text, TEXT_VALUE_MAX) } : null;
  },
  text: (value, _row, fmt) => sizesOrPriceText(value, fmt) ?? null,
  stale(value, _row, fmt) {
    const text = sizesOrPriceText(value, fmt);
    return text ? fmt.t('assistant.result.staleState', { state: text }) : null;
  },
};

/** «+ Grande $ 11.000»: a new size and its price (« · Pausada» when it starts paused). */
function newSizeText(value: INewChoiceValue, fmt: ICardFormat): string {
  const size = `+ ${sizeText(value, fmt)}`;
  return value.active
    ? size
    : `${size}${SIZE_SEPARATOR}${fmt.t('assistant.proposal.value.paused')}`;
}

/**
 * A new size of a product (plan P4-1): its name and price; its row names the
 * product only (the row's `sizeName` is the new size's, already in the value).
 */
const NEW_SIZE_PRESENTER: IRowPresenter = {
  fieldKey: 'assistant.proposal.field.newSize',
  diff: (value, row, fmt) =>
    isNewChoiceValue(value)
      ? { text: clampText(newSizeText(value, fmt), TEXT_VALUE_MAX) }
      : GENERIC_PRESENTER.diff(value, row, fmt),
  text: (value, _row, fmt) =>
    isNewChoiceValue(value) ? newSizeText(value, fmt) : null,
  namesSize: (row) => isNewChoiceValue(row.to),
};

/** The values of a card string that a catalog text helper passes. */
function cardParams(params: Record<string, unknown> = {}): ICardTextParams {
  const values: ICardTextParams = {};
  for (const [name, value] of Object.entries(params))
    if (typeof value === 'string' || typeof value === 'number')
      values[name] = value;
  return values;
}

/**
 * «Lun a vie · 12:00 a 15:00 y sáb · 20:00 a 23:59»: a schedule in the Menú
 * badges' words, one window after another (plan P4-2); «Todo el día» without
 * one, and the editor's «Sin horarios válidos» when only legacy holiday rows
 * are left.
 */
function scheduleText(value: IScheduleValue, fmt: ICardFormat): string {
  const t: TTranslate = (key, params) => fmt.t(key, cardParams(params));
  const lines = scheduleSummary(value.hourRanges)
    .map((line) => scheduleBadgeText(t, [line]))
    .filter((line): line is string => line !== null);
  if (lines.length) return capitalize(fmt.list(lines));
  return fmt.t(
    value.hourRanges?.length
      ? 'menuPage.schedule.noValidHours'
      : 'menuPage.schedule.always'
  );
}

/** A product's or a category's own hours (plan P4-2). */
const SCHEDULE_PRESENTER: IRowPresenter = {
  fieldKey: 'assistant.proposal.field.schedule',
  diff: (value, row, fmt) =>
    isScheduleValue(value)
      ? { text: clampText(scheduleText(value, fmt), TEXT_VALUE_MAX) }
      : GENERIC_PRESENTER.diff(value, row, fmt),
  text: (value, _row, fmt) =>
    isScheduleValue(value) ? scheduleText(value, fmt) : null,
  stale: (value, _row, fmt) =>
    isScheduleValue(value)
      ? fmt.t('assistant.result.staleState', {
          state: scheduleText(value, fmt),
        })
      : null,
};

/**
 * A copy of a product (plan P4-3): it starts hidden in every branch; its row
 * names the copy and, under it, the product it copies.
 */
const COPY_PRESENTER: IRowPresenter = {
  fieldKey: 'assistant.proposal.field.copy',
  diff: (value, row, fmt) =>
    isCopyValue(value)
      ? {
          pill: clampText(
            fmt.t('assistant.proposal.value.copyHidden'),
            PILL_MAX
          ),
          tone: 'neutral',
        }
      : GENERIC_PRESENTER.diff(value, row, fmt),
  text: (value, _row, fmt) =>
    isCopyValue(value)
      ? fmt.t('assistant.proposal.value.copyOf', { name: value.sourceName })
      : null,
  subtitle: (row, fmt) =>
    isCopyValue(row.to)
      ? fmt.t('assistant.proposal.value.copyOf', { name: row.to.sourceName })
      : null,
};

/** A product's preparation notes (plan P4-4), titled like the Menú editor. */
const NOTES_PRESENTER = textPresenter('menuPage.recipe.title');

/** The stock units this release names (`StockUnit`, plan P5-1). */
const STOCK_UNITS: readonly string[] = Object.values(StockUnit);

/**
 * «kg», «unidad» / «unidades»: a stock unit as `quantity` of it reads (plan
 * P5-3: `assistant.stock.unit.*`, singular for exactly one, plural for any
 * other quantity, «0,5 unidades» included). A unit this release does not
 * know reads as its code.
 */
export function stockUnitText(
  unit: string,
  quantity: number,
  fmt: ICardFormat
): string {
  if (!STOCK_UNITS.includes(unit)) return unit;
  return fmt.tc(
    `assistant.stock.unit.${unit}`,
    Math.abs(quantity) === 1 ? 1 : 2
  );
}

/** «0,2 kg», «3 unidades»: a stock quantity in its unit, never split across lines. */
function stockQuantityText(
  quantity: number,
  unit: TStockUnit,
  fmt: ICardFormat
): string {
  return `${fmt.quantity(quantity)}\u00a0${stockUnitText(unit, quantity, fmt)}`;
}

/** What a purchase and a loss are called (plan P5-3). */
const STOCK_MOVEMENT_KEYS: Readonly<Record<string, string>> = {
  purchase: 'assistant.proposal.value.movement.purchase',
  loss: 'assistant.proposal.value.movement.loss',
};

/**
 * «+10 kg · Compra», «−2 kg · Pérdida»: a purchase or a loss (plan P5-3); a
 * movement this release does not know shows its quantity alone.
 */
function stockMoveText(value: IStockMoveValue, fmt: ICardFormat): string {
  const sign = value.delta < 0 ? '−' : '+';
  const delta = `${sign}${stockQuantityText(
    Math.abs(value.delta),
    value.unit,
    fmt
  )}`;
  const movementKey = Object.prototype.hasOwnProperty.call(
    STOCK_MOVEMENT_KEYS,
    value.movement
  )
    ? STOCK_MOVEMENT_KEYS[value.movement]
    : null;
  return movementKey
    ? fmt.t('assistant.proposal.value.stockMove', {
        delta,
        movement: fmt.t(movementKey),
      })
    : delta;
}

/**
 * A purchase or a loss of a stock item (plan P5-3): what it adds or takes
 * away, with no value before (sales move the stock all day long).
 */
const STOCK_MOVE_PRESENTER: IRowPresenter = {
  fieldKey: 'assistant.proposal.field.quantity',
  diff: (value, row, fmt) =>
    isStockMoveValue(value)
      ? { text: clampText(stockMoveText(value, fmt), TEXT_VALUE_MAX) }
      : GENERIC_PRESENTER.diff(value, row, fmt),
  text: (value, _row, fmt) =>
    isStockMoveValue(value) ? stockMoveText(value, fmt) : null,
};

/** «12 kg»: a stock quantity value in its unit. */
function stockAmountText(value: IStockQuantityValue, fmt: ICardFormat): string {
  return stockQuantityText(value.quantity, value.unit, fmt);
}

/** A stock item's quantity, or its minimum stock (plan P5-3): «12 kg». */
function stockAmountPresenter(fieldKey: string): IRowPresenter {
  return {
    fieldKey,
    diff: (value, row, fmt) =>
      isStockQuantityValue(value)
        ? { text: clampText(stockAmountText(value, fmt), TEXT_VALUE_MAX) }
        : GENERIC_PRESENTER.diff(value, row, fmt),
    text: (value, _row, fmt) =>
      isStockQuantityValue(value) ? stockAmountText(value, fmt) : null,
    stale: (value, _row, fmt) =>
      isStockQuantityValue(value)
        ? fmt.t('assistant.result.staleState', {
            state: stockAmountText(value, fmt),
          })
        : null,
  };
}

/** A physical count: the stock now, then the counted total (plan P5-3). */
const STOCK_COUNT_PRESENTER = stockAmountPresenter(
  'assistant.proposal.field.quantity'
);

/**
 * A stock item's minimum stock (plan P5-3). Its stock item's name and «2 l →
 * 3 l» alone would read as the stock itself: a row titled by the stock item
 * says it is the minimum (P5-ADM-E2E, the activity).
 */
const STOCK_MINIMUM_PRESENTER: IRowPresenter = {
  ...stockAmountPresenter('assistant.proposal.field.minimum'),
  subjectNeedsField: true,
};

/** «$ 1.200 por kg»: what a new stock item costs per unit (plan P5-3). */
function newStockItemCostText(
  value: INewStockItemValue,
  fmt: ICardFormat
): string {
  return fmt.t('assistant.proposal.value.newStockItem', {
    cost: fmt.money(value.cost),
    unit: stockUnitText(value.unit, 1, fmt),
  });
}

/**
 * A new stock item (plan P5-3): its row names it and shows its cost per
 * unit; under its name, that it is new and the stock it starts with.
 */
const NEW_STOCK_ITEM_PRESENTER: IRowPresenter = {
  fieldKey: 'assistant.proposal.value.newStockItemTitle',
  diff: (value, row, fmt) =>
    isNewStockItemValue(value)
      ? { text: clampText(newStockItemCostText(value, fmt), TEXT_VALUE_MAX) }
      : GENERIC_PRESENTER.diff(value, row, fmt),
  // An activity row is labelled with its name only: its text says it is new.
  text: (value, _row, fmt) =>
    isNewStockItemValue(value)
      ? `${fmt.t(
          'assistant.proposal.value.newStockItemTitle'
        )} · ${newStockItemCostText(value, fmt)}`
      : null,
  subtitle(row, fmt) {
    if (!isNewStockItemValue(row.to)) return null;
    const title = fmt.t('assistant.proposal.value.newStockItemTitle');
    if (row.to.quantity <= 0) return title;
    const stock = fmt.t('assistant.proposal.field.quantity');
    return `${title} · ${stock} ${stockQuantityText(
      row.to.quantity,
      row.to.unit,
      fmt
    )}`;
  },
  // Its value never changes: it is stale when its name was taken or freed
  // since it was reviewed (W22 F6); its row in the fresh proposal says which.
  stale: (_value, row, fmt) =>
    row?.reasons.includes('NAME_EXISTS')
      ? fmt.t('assistant.result.staleNameExistsStock')
      : null,
};

/** «kg», «unidades»: a stock item's unit (plan P5-3: an `ITextValue` of its code). */
function unitValueText(code: string, fmt: ICardFormat): string {
  return stockUnitText(code, 2, fmt);
}

/**
 * A stock item's unit (plan P5-3): «kg» → «g»; a row titled by the stock
 * item says it is the unit.
 */
const STOCK_UNIT_PRESENTER: IRowPresenter = {
  fieldKey: 'assistant.proposal.field.unit',
  subjectNeedsField: true,
  diff(value, row, fmt) {
    if (!isTextValue(value)) return GENERIC_PRESENTER.diff(value, row, fmt);
    return value.text
      ? { text: clampText(unitValueText(value.text, fmt), TEXT_VALUE_MAX) }
      : null;
  },
  text: (value, _row, fmt) =>
    isTextValue(value) && value.text ? unitValueText(value.text, fmt) : null,
  stale: (value, _row, fmt) =>
    isTextValue(value) && value.text
      ? fmt.t('assistant.result.staleState', {
          state: unitValueText(value.text, fmt),
        })
      : null,
};

/**
 * A stock item's cost per unit (plan P5-3): money; a row titled by the stock
 * item says it is the cost, not a price.
 */
const STOCK_COST_PRESENTER: IRowPresenter = {
  fieldKey: 'assistant.proposal.field.cost',
  subjectNeedsField: true,
  diff: (value, row, fmt) =>
    isMoneyValue(value)
      ? { text: clampText(fmt.money(value.amount), TEXT_VALUE_MAX) }
      : GENERIC_PRESENTER.diff(value, row, fmt),
  text: (value, _row, fmt) =>
    isMoneyValue(value) ? fmt.money(value.amount) : null,
  stale: (value, _row, fmt) =>
    isMoneyValue(value)
      ? fmt.t('assistant.result.staleNow', { price: fmt.money(value.amount) })
      : null,
};

/** The recipe lines a `recipe` row names; the rest are counted (plan P5-3). */
const RECIPE_LINES_SHOWN = 6;

/** What separates the lines of a recipe in a value. */
const RECIPE_SEPARATOR = ' · ';

/** U+200F RIGHT-TO-LEFT MARK. */
const RLM = '\u200f';

/**
 * «Carne 0,2 kg · Pan rallado 0,05 kg»: a recipe's stock items and what one
 * unit sold uses of each, the first 6, then «y {n} más» (plan P5-3); «Sin
 * receta» for none. Each name is isolated like any value in a sentence. In a
 * right-to-left language the lines start with a right-to-left mark: the
 * widget lays a value out by its first strong letter, and a stock item named
 * in Latin letters would turn the whole value left to right, splitting each
 * line from its unit («Harina 0,4 … كغ»; P5-ADM-E2E).
 */
function recipeText(value: IRecipeValue, fmt: ICardFormat): string {
  if (!value.lines.length) return fmt.t('assistant.proposal.value.noRecipe');
  const shown = value.lines.slice(0, RECIPE_LINES_SHOWN).map((line) => {
    const { name } = bidiIsolateParams(fmt.locale, { name: line.name });
    return `${name} ${stockQuantityText(line.quantity, line.unit, fmt)}`;
  });
  const lines = shown.join(RECIPE_SEPARATOR);
  const list = isAssistantRtl(fmt.locale) ? `${RLM}${lines}` : lines;
  const more = value.lines.length - shown.length;
  return more > 0
    ? fmt.t('assistant.proposal.value.orderMore', {
        list,
        n: fmt.number(more),
      })
    : list;
}

/** The recipe of a product, a size or an option choice (plan P5-3). */
const RECIPE_PRESENTER: IRowPresenter = {
  fieldKey: 'assistant.proposal.field.recipe',
  diff: (value, row, fmt) =>
    isRecipeValue(value)
      ? { text: clampText(recipeText(value, fmt), TEXT_VALUE_MAX) }
      : GENERIC_PRESENTER.diff(value, row, fmt),
  text: (value, _row, fmt) =>
    isRecipeValue(value) ? recipeText(value, fmt) : null,
  stale: (value, _row, fmt) =>
    isRecipeValue(value)
      ? fmt.t('assistant.result.staleState', { state: recipeText(value, fmt) })
      : null,
};

/** One option group of a product's link list (plan F3). */
export type TGroupLink = IGroupLinksValue['groups'][number];

/** One link of a product's list: an existing group by id, a new one by its row key. */
export function linkKey(group: TGroupLink): string {
  return group.id !== null ? `id:${group.id}` : `new:${group.groupKey}`;
}

/**
 * The new groups a product's `groups` row gives it in place of a group of
 * the same name it loses: a copy of that group for this product (plan P3-2,
 * the Menú's «Usar una copia solo aquí»), which would read unchanged.
 */
function copyKeysOf(row: IAssistantRow): Set<string> {
  if (!row.from || !isGroupLinksValue(row.from) || !isGroupLinksValue(row.to))
    return new Set();
  const after = new Set(row.to.groups.map(linkKey));
  const lost = new Set(
    row.from.groups
      .filter((group) => !after.has(linkKey(group)))
      .map((group) => group.name)
  );
  return new Set(
    row.to.groups
      .filter((group) => group.id === null && lost.has(group.name))
      .map(linkKey)
  );
}

/**
 * «Tamaño, Leche»: a product's option groups in their order, a copy after the
 * row as «Leches (copia)»; null for none.
 */
function groupNamesText(
  value: IGroupLinksValue,
  row: IAssistantRow,
  fmt: ICardFormat
): string | null {
  if (!value.groups.length) return null;
  const copies = value === row.to ? copyKeysOf(row) : new Set<string>();
  return value.groups
    .map((group) =>
      copies.has(linkKey(group))
        ? fmt.t('assistant.proposal.value.groupCopy', { name: group.name })
        : group.name
    )
    .join(', ');
}

/**
 * What a product's `groups` row does to its option groups (plan P3-4):
 * `added` / `removed` when it gets or loses exactly one group and nothing
 * else changes but the order, `reordered` when it keeps the same groups in
 * another order; null for anything else (several groups, or not a link
 * list).
 */
export type TGroupLinkChange =
  | { kind: 'added' | 'removed'; group: TGroupLink }
  | { kind: 'reordered' };

export function groupLinkChange(row: IAssistantRow): TGroupLinkChange | null {
  if (
    row.field !== 'groups' ||
    !row.from ||
    !isGroupLinksValue(row.from) ||
    !isGroupLinksValue(row.to)
  )
    return null;
  const before = new Set(row.from.groups.map(linkKey));
  const after = new Set(row.to.groups.map(linkKey));
  const added = row.to.groups.filter((group) => !before.has(linkKey(group)));
  const removed = row.from.groups.filter((group) => !after.has(linkKey(group)));
  if (added.length === 1 && removed.length === 0)
    return { kind: 'added', group: added[0] };
  if (removed.length === 1 && added.length === 0)
    return { kind: 'removed', group: removed[0] };
  return added.length === 0 && removed.length === 0
    ? { kind: 'reordered' }
    : null;
}

/** «+ Leche» / «− Leche»: the one group a product gets or loses (plan P3-4). */
function groupLinkDelta(row: IAssistantRow, fmt: ICardFormat): string | null {
  const change = groupLinkChange(row);
  if (!change || change.kind === 'reordered') return null;
  const sign = change.kind === 'added' ? '+' : '−';
  const { name } = bidiIsolateParams(fmt.locale, { name: change.group.name });
  return clampText(`${sign} ${name}`, DELTA_MAX);
}

/**
 * A product's option groups (plan P3-4): the names before and after (a new
 * group by its name, a copy that replaces its group marked as one), and
 * «+ Leche» / «− Leche» when exactly one is added or taken out.
 */
const GROUPS_PRESENTER: IRowPresenter = {
  fieldKey: 'assistant.proposal.field.groups',
  diff(value, row, fmt) {
    if (!isGroupLinksValue(value))
      return GENERIC_PRESENTER.diff(value, row, fmt);
    const text = groupNamesText(value, row, fmt);
    return text ? { text: clampText(text, TEXT_VALUE_MAX) } : null;
  },
  text: (value, row, fmt) =>
    isGroupLinksValue(value) ? groupNamesText(value, row, fmt) : null,
  delta: groupLinkDelta,
};

/**
 * The presenter of a row: by its field and, where the field holds a value
 * this release reads only for some subjects, by its entity;
 * `GENERIC_PRESENTER` for anything this release does not know.
 */
export function presenterOf(row: IAssistantRow): IRowPresenter {
  const entity = rowEntity(row);
  switch (row.field) {
    case 'price':
      if (entity === 'choice') return CHOICE_PRICE_PRESENTER;
      return PRICE_ENTITIES.includes(entity)
        ? PRICE_PRESENTER
        : GENERIC_PRESENTER;
    case 'availability':
      if (PAUSED_ENTITIES.includes(entity))
        return PAUSED_AVAILABILITY_PRESENTER;
      return AVAILABILITY_ENTITIES.includes(entity)
        ? AVAILABILITY_PRESENTER
        : GENERIC_PRESENTER;
    case 'name':
      return NAME_PRESENTER;
    case 'description':
      return DESCRIPTION_PRESENTER;
    case 'create':
      return CREATE_PRESENTER;
    case 'newCategory':
      return entity === 'category' ? NEW_CATEGORY_PRESENTER : GENERIC_PRESENTER;
    case 'posOnly':
      return entity === 'category' ? POS_ONLY_PRESENTER : GENERIC_PRESENTER;
    case 'category':
      return entity === 'item' ? MOVE_PRESENTER : GENERIC_PRESENTER;
    case 'order':
      if (entity === 'item') return SIZE_ORDER_PRESENTER;
      return ORDER_ENTITIES.includes(entity)
        ? ORDER_PRESENTER
        : GENERIC_PRESENTER;
    case 'groups':
      return entity === 'item' ? GROUPS_PRESENTER : GENERIC_PRESENTER;
    case 'newGroup':
      return entity === 'group' ? NEW_GROUP_PRESENTER : GENERIC_PRESENTER;
    case 'rules':
      return entity === 'group' ? RULES_PRESENTER : GENERIC_PRESENTER;
    case 'newChoice':
      return entity === 'choice' ? NEW_CHOICE_PRESENTER : GENERIC_PRESENTER;
    case 'remove':
      if (entity === 'choice') return CHOICE_REMOVE_PRESENTER;
      return entity === 'size' ? SIZE_REMOVE_PRESENTER : GENERIC_PRESENTER;
    case 'sizes':
      return entity === 'item' ? SIZES_PRESENTER : GENERIC_PRESENTER;
    // A new size has no id yet: its row is the product's (plan P4-1).
    case 'newSize':
      return PRODUCT_ENTITIES.includes(entity)
        ? NEW_SIZE_PRESENTER
        : GENERIC_PRESENTER;
    case 'schedule':
      return SCHEDULE_ENTITIES.includes(entity)
        ? SCHEDULE_PRESENTER
        : GENERIC_PRESENTER;
    case 'copy':
      return entity === 'item' ? COPY_PRESENTER : GENERIC_PRESENTER;
    case 'notes':
      return entity === 'item' ? NOTES_PRESENTER : GENERIC_PRESENTER;
    case 'stockMove':
      return entity === 'stockItem' ? STOCK_MOVE_PRESENTER : GENERIC_PRESENTER;
    case 'stockCount':
      return entity === 'stockItem' ? STOCK_COUNT_PRESENTER : GENERIC_PRESENTER;
    case 'newStockItem':
      return entity === 'stockItem'
        ? NEW_STOCK_ITEM_PRESENTER
        : GENERIC_PRESENTER;
    case 'unit':
      return entity === 'stockItem' ? STOCK_UNIT_PRESENTER : GENERIC_PRESENTER;
    case 'cost':
      return entity === 'stockItem' ? STOCK_COST_PRESENTER : GENERIC_PRESENTER;
    case 'minimum':
      return entity === 'stockItem'
        ? STOCK_MINIMUM_PRESENTER
        : GENERIC_PRESENTER;
    case 'recipe':
      return entity === 'recipe' ? RECIPE_PRESENTER : GENERIC_PRESENTER;
    default:
      return GENERIC_PRESENTER;
  }
}

/**
 * The presenter of a value whose row is not at hand (a stale line of a row
 * that left the proposal): by the v1 value shapes, which keep their meaning
 * in every later shape (plan F3 forward-compat rule).
 */
export function presenterOfValue(value: TAssistantValue): IRowPresenter {
  if (isAvailabilityValue(value)) return AVAILABILITY_PRESENTER;
  if (isPriceValue(value)) return PRICE_PRESENTER;
  return GENERIC_PRESENTER;
}

/**
 * «Pizza · Grande»: what a row changes, read through its presenter; without
 * the size when the row's value names it (a new size, plan P4-1).
 */
export function rowLabel(row: IAssistantRow, presenter: IRowPresenter): string {
  return presenter.namesSize?.(row) ? row.itemName : rowItemLabel(row);
}

/**
 * «Napolitana · Orden de las variantes»: a row's label where nothing goes
 * under it («Cambios del asistente»), with its field title when its subject
 * alone does not say what changes; the subject is then isolated like a field
 * title's size.
 */
export function rowLabelWithField(
  row: IAssistantRow,
  presenter: IRowPresenter,
  fmt: ICardFormat
): string {
  const subject = rowLabel(row, presenter);
  if (!presenter.subjectNeedsField) return subject;
  const isolated = bidiIsolateParams(fmt.locale, { subject });
  return `${isolated.subject} · ${fmt.t(presenter.fieldKey)}`;
}
